import React, { useEffect, useRef, useState } from 'react';
import 'twin.macro';
import { CenterRotatedCell } from '@teetsh/app/src/components/CenterRotatedCell';
import { subtitleStyle } from '@teetsh/app/src/containers/Programmations/ProgV2/Print/styles';
import tw from 'twin.macro';
import H2 from '@teetsh/app/src/components/Typography/Headings/H2';
import { RowTitleOrientation } from '@teetsh/app/src/containers/Programmations/ProgV2/Print/types';
import { BreakBeforePageDivider } from '../../Preview/BreakBeforePageDivider';

interface Props {
  children: [React.ReactNode, React.ReactNode];
  hasFirstColSubtitles?: boolean;
  sectionTitle?: string;
  shortSectionTitle?: string;
  matiereTitle?: string;
  rowTitleOrientation?: RowTitleOrientation;
  breakBefore?: boolean;
}

export function MatiereLayout(props: Props) {
  const {
    children,
    hasFirstColSubtitles,
    matiereTitle,
    sectionTitle,
    shortSectionTitle,
  } = props;
  const cellRef = useRef<HTMLDivElement>(null);
  const [cellWidth, setCellWidth] = useState('auto');

  // We force a recompute on firefox because it doesn't get the width right
  // https://www.notion.so/teetsh/Probl-me-firefox-writing-mode-flex-2d2d272cd34e455c94ba584a992f25a1
  useEffect(() => {
    if (cellRef.current) {
      const width = cellRef.current.getBoundingClientRect().width;
      if (width === 0) {
        // Additional check to handle Firefox's issue
        setCellWidth('max-content'); // Set a default or minimum width if 0 is detected
      } else {
        setCellWidth(`${width}px`);
      }
    }
  }, [children]); // Depend on children as changing content might affect size

  return (
    <div css={[props.breakBefore && tw`break-before-page`]}>
      {props.breakBefore && <BreakBeforePageDivider tw="mb-2" />}
      {matiereTitle && (
        <H2 tw="mb-3 print:break-after-avoid">{matiereTitle}</H2>
      )}
      {props.rowTitleOrientation === 'horizontal' && (
        <div
          className="break-inside-avoid break-after-avoid"
          tw="flex items-baseline gap-x-2 mb-2 font-bold text-xl"
        >
          {sectionTitle}
        </div>
      )}
      <div
        className="break-inside-avoid break-after-avoid"
        tw="flex gap-x-2 mb-2"
      >
        {props.rowTitleOrientation === 'vertical' && (
          <div
            ref={cellRef}
            tw="flex overflow-hidden"
            style={{ width: cellWidth }}
          >
            <CenterRotatedCell tw="relative" active={true}>
              <div tw="text-base text-center font-bold invisible">A</div>
              {hasFirstColSubtitles && (
                <div css={[subtitleStyle, tw`invisible`]}>B</div>
              )}
              {shortSectionTitle && (
                // Mettre en absolute ça permet de bien centrer lorsque la colonne est
                // sur 2 lignes avec le sous titre
                <div
                  css={[
                    tw`absolute left-1/2 flex justify-center -translate-x-1/2 text-base font-bold`,
                    // Comme on a pas la place de mettre un mot plus de 4 caractères
                    // si ça fait plus de 4 chars on text align à gauche sinon avec le overflow-hidden on affiche seulement le milieu du mot
                    shortSectionTitle.length > 4 && tw`top-0`,
                    shortSectionTitle.length <= 4 &&
                      tw`top-1/2 -translate-y-1/2`,
                  ]}
                >
                  {shortSectionTitle}
                </div>
              )}
            </CenterRotatedCell>
          </div>
        )}
        {children[0]}
      </div>
      <div tw="space-y-3 mt-3">{children[1]}</div>
    </div>
  );
}
